import type { HttpMethod } from '~/shared/types/network'

import type { RequestOptions } from '~/shared/api'
import { ApiService } from '~/shared/api'
import type { ApiPaginatedResponse } from '~/shared/types/api'

import type { SERVICES_PREFIXES_LIST } from '~/shared/const/request-services'
import { API_PREFIX } from '~/shared/const/request-services'
import { isString } from '~/shared/utils/guards'
import { unwrapObject } from '~/shared/utils/response'

export type ServicesPrefixes = (typeof SERVICES_PREFIXES_LIST)[number]

export type RequestListResponse<T = unknown> = {
  currentPage: number
  totalPages: number
  totalItems: number
  data: T[]
  isRedirect?: boolean
  redirectUrl?: string
}

export class RequestRestService extends ApiService {
  public async getList<T = unknown>(
    service: ServicesPrefixes,
    params?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<RequestListResponse<T>> {
    const res = await this.http.request<ApiPaginatedResponse<T>>({
      url: `${API_PREFIX}/${service}`,
      params,
      signal
    })

    // Проверяем статус 301 (редирект)
    if (res.status === 301) {
      const redirect = unwrapObject(res.data)
      return {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        data: [],
        isRedirect: true,
        redirectUrl: redirect && isString(redirect.redirect_url) ? redirect.redirect_url : undefined
      }
    }

    const { current_page: currentPage, last_page: totalPages, total, data } = res.data.data

    return {
      currentPage,
      totalPages,
      totalItems: total,
      data
    }
  }

  public async getItem<T = any>(
    service: ServicesPrefixes,
    slug?: string | number,
    params: Record<string, unknown> = {}
  ): Promise<T> {
    const res = await this.http.request<T>({
      url: `${API_PREFIX}/${service}${slug ? '/' + slug : ''}`,
      params,
    })

    return res.data
  }

  public async updateItem<T = any>(
    service: ServicesPrefixes,
    slug: string,
    data: object
  ): Promise<T> {
    const res = await this.http.request<T>({
      method: 'PUT' as HttpMethod,
      data,
      url: `${API_PREFIX}/${service}/${slug}`,
    })
    if (Array.isArray(res.data)) {
      throw new TypeError('Server sent unhandled data')
    }
    return res.data
  }

  public async createItem<T = any>(
    service: ServicesPrefixes,
    data?: object,
    options?: Partial<RequestOptions>,
    slug?: string
  ): Promise<T> {
    const res = await this.http.request<T>({
      method: 'POST' as HttpMethod,
      data,
      ...options,
      url: slug ? `${API_PREFIX}/${service}/${slug}` : `${API_PREFIX}/${service}`,
    })
    return res.data
  }

  public async deleteItem<T = any>(service: ServicesPrefixes, slug: string): Promise<T> {
    const res = await this.http.request<T>({
      method: 'POST' as HttpMethod,
      url: `${API_PREFIX}/${service}/${slug}/delete`,
    })
    return res.data
  }
}
